import type { FeatureConfig } from '@skynet/feature-config';
import { SendOutlined } from '@ant-design/icons';
import {
  TRADING_GROUP_DETAIL_ROUTE_PATH,
  TRADING_GROUPS_LIST_URL,
  TRADING_GROUPS_ROUTE_PATH,
} from './routes';

/**
 * Feature manifest for the admin Trading Groups list/archive and group detail pages. The
 * dashboard shell reads this single source of truth instead of hardcoding the
 * route, navigation entry, header label and Cerbos action across its routing,
 * navigation, header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing this config for its
 * nav/header metadata never pulls the page implementation into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this package's own
 * `./routes`.
 *
 * The feature was renamed from "Telegram Groups" to "Trading Groups", but only
 * the labels moved: `section`, `nav.key` and the route path are the identifier
 * the backend Cerbos policy is written against. `useAuthorizedNavigation`
 * derives the checked action from `nav.key` and `useSection`'s `getDefaults`
 * derives it from `section` (both as `read:admin:<section>`), so renaming
 * either one hides the nav item and breaks default-section resolution for every
 * operator until the PDP policy is renamed too.
 */
export const tradingGroupsConfig = {
  domain: 'Admin',
  section: 'telegram-groups',
  route: {
    path: TRADING_GROUPS_ROUTE_PATH,
    load: async () => ({ default: (await import('./TradingGroupsPage')).TradingGroupsPage }),
  },
  detailRoute: {
    path: TRADING_GROUP_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./TradingGroupPage')).TradingGroupPage }),
  },
  nav: {
    key: 'admin.telegram-groups',
    label: 'Trading Groups',
    icon: <SendOutlined />,
    url: TRADING_GROUPS_LIST_URL,
  },
  header: { label: 'Trading Groups' },
  cerbosAction: 'read:admin:telegram-groups',
  skipWsLoginPaths: [TRADING_GROUPS_LIST_URL],
} as const satisfies FeatureConfig;
